// The app's data: what is stored on the phone (IndexedDB) and what group members send each other.

export type Kind = "fish" | "game";

export interface Loc {
  lat: number;
  lon: number;
  /** GPS accuracy in metres, when known. */
  acc?: number | null;
  /** Where the position came from. Missing on older catches. */
  src?: "gps" | "photo" | "map";
}

/** Weather at the catch's place and hour (Open-Meteo). */
export interface Weather {
  status: "ok";
  temp: number | null;
  humidity: number | null;
  precip: number | null;
  cloud: number | null;
  pressure: number | null;
  /** Pressure change over the previous 3 hours, hPa. */
  pressure3h?: number;
  wind: number | null;
  windDir: number | null;
  gust: number | null;
  /** WMO weather code. */
  code: number | null;
  source: string;
  /** The place and time this weather was looked up for, to notice when the catch is moved. */
  forLat: number;
  forLon: number;
  forTime: number;
}
export type CatchWeather = Weather | { status: "pending" } | null;

/** Water temperature: your own reading, the nearest lake station, the sea model, or nothing found. */
export interface Water {
  src: "manual" | "station" | "sea" | "none";
  value?: number;
  station?: string;
  km?: number;
  forLat?: number;
  forLon?: number;
  forTime?: number;
  checked?: number;
}

export interface Catch {
  id: string;
  kind: Kind;
  /** The name as typed. */
  species: string;
  /** A known species code (e.g. "pike"), so the name shows in every language. */
  speciesId?: string;
  time: number;
  weightKg: number | null;
  lengthCm: number | null;
  /** A method code, e.g. "lure" or "rifle"; "" when not given. */
  method: string;
  gear: string;
  released: boolean;
  place: string;
  notes: string;
  loc: Loc | null;
  weather: CatchWeather;
  water?: Water;
  photoIds: string[];
  createdAt: number;
  updatedAt?: number;
}

export interface Photo {
  id: string;
  full: Blob;
  thumb: Blob;
}

export type PinType = "spot" | "stand" | "boat" | "camera" | "parking" | "other";

export interface Pin {
  id: string;
  type: PinType;
  name: string;
  notes: string;
  lat: number;
  lon: number;
  createdAt: number;
  updatedAt?: number;
  /** Set when the pin came from a file or a group message. */
  source?: "gpx" | "kml" | "group";
}

export type LatLon = [lat: number, lon: number];

export interface Route {
  id: string;
  name: string;
  points: LatLon[];
  createdAt: number;
  source?: "gpx" | "kml";
}

export interface Group {
  /** Made from the token, so the relay can check access without storing anything. */
  id: string;
  /** The AES-GCM key (base64url). Only in invite links and on members' phones. */
  key: string;
  /** Made from the key; proves to the relay that you have the key. */
  token: string;
  name: string;
  createdAt: number;
  /** Server time of the newest message fetched. */
  lastTs: number;
  unread: number;
  lastText: string;
  lastAt: number;
  /** The relay refused the token (the group's key no longer matches). */
  broken?: boolean;
}

/* ---------- group messages (encrypted on the phone before sending) ---------- */

/** A catch as shared to a group: only what the sender chose to share. */
export interface SharedCatch {
  species: string;
  kind: Kind;
  weightKg: number | null;
  lengthCm: number | null;
  released: boolean;
  time: number;
  place: string;
  temp?: number | null;
  wind?: number | null;
  windDir?: number | null;
  water: number | null;
  loc: { lat: number; lon: number } | null;
}
export interface Stand { id: string; name: string; lat: number; lon: number }
export interface Reservation { standId: string; date: string; by: string; byName: string; ts: number }
export interface Person { id: string; name: string }

export type Payload =
  | { type: "text"; text: string }
  | { type: "photo"; image: string; text: string }
  | { type: "catch"; image: string | null; catch: SharedCatch }
  | { type: "pin"; pin: Pick<Pin, "type" | "name" | "notes" | "lat" | "lon"> }
  | { type: "join" }
  // club stand board (club.ts)
  | { type: "stand"; op: "add"; stand: Stand }
  | { type: "stand"; op: "remove"; stand: Pick<Stand, "id"> }
  | { type: "res"; op: "reserve" | "cancel"; standId: string; date: string; for?: Person }
  | { type: "clubstate"; stands: Stand[]; res: Reservation[]; area?: string }
  // the club's hunting area from Oma riista (clubarea.ts): its area identifier, "" when removed
  | { type: "area"; areaId: string }
  // photo feed (feed.ts)
  | { type: "like"; postId: string; on: boolean }
  | { type: "comment"; postId: string; text: string };

export type MessageType = Payload["type"];

/** Who sent a message and when, added to every payload before it is encrypted. */
export interface Sender {
  from: string;
  name: string;
  sentAt: number;
}

/** A decrypted message as stored on the phone. */
export type GroupMessage = Payload & Sender & {
  id: string;
  /** Server time, used for ordering. */
  ts: number;
  groupId: string;
};

/** What the relay stores and returns: only scrambled data. */
export interface RelayMessage {
  id: string;
  ts: number;
  ct: string;
  iv: string;
}

/* ---------- other stored records ---------- */

/** Settings the service worker needs to word notifications (it can't read localStorage). */
export interface SwSettings {
  id: "settings";
  lang: string;
  meId: string;
}

/** An automatic copy of catches, pins and routes (safety.ts). Photos are left out. */
export interface Snapshot {
  id: string;
  at: number;
  reason: "daily" | "import" | "restore";
  catches: Catch[];
  pins: Pin[];
  routes: Route[];
}

/** A route point: latitude, longitude and time (ms). */
export type TrackPoint = [lat: number, lon: number, t: number];

/** A trip (trip mode): what you were after, the route recorded while the app was open, and when. Catches made
 *  during a trip aren't linked by id; they are the ones whose time falls between start and end. */
export interface Trip {
  id: string;
  startedAt: number;
  /** null while the trip is going on. */
  endedAt: number | null;
  /** What you were after; null = anything. */
  target: { kind: Kind; speciesId?: string; species?: string } | null;
  track: TrackPoint[];
  /** Length of the route, km. */
  km: number;
  /** The weather where the trip started. */
  startWeather?: Weather | null;
}

/** The IndexedDB object stores and what each one holds. */
export interface Stores {
  catches: Catch;
  photos: Photo;
  pins: Pin;
  routes: Route;
  groups: Group;
  messages: GroupMessage;
  kv: SwSettings;
  snapshots: Snapshot;
  trips: Trip;
}
export type StoreName = keyof Stores;

/** Feedback sent from the app (app/feedback.ts → server/feedback.ts). */
export interface FeedbackReport {
  text: string;
  /** Optional, for a reply. */
  email: string;
  version: string;
  platform: string;
  app: string;
  lang: string;
  /** Screen size, e.g. "390x844". */
  screen: string;
  /** The tab that was open. */
  view: string;
}

/** Your account as the server reports it (/api/account). */
export interface AccountInfo {
  /** The account id: also the id purchases are tied to (RevenueCat app user id), so Plus follows the account. */
  id: string;
  email: string;
  /** May ReelHunt email you news (a separate, optional consent). */
  news: boolean;
  created: number;
  /** The newest cloud backup, if any. */
  backup: { at: number; bytes: number; catches: number; pins: number } | null;
}

/** The anonymous daily usage report (app/usage.ts sends it, server/usage.ts stores it). */
export interface UsageReport {
  /** A random id made on the phone; the server keeps only a hash of it together with the month. */
  id: string;
  platform: "android" | "ios" | "desktop" | "other";
  app: "play" | "installed" | "browser";
  lang: string;
  catches: number;
  pins: number;
  groups: number;
  pushMsgs: boolean;
  pushTips: boolean;
  profile: boolean;
}
export const DB_NAME = "hunt-fish-log", DB_VERSION = 6;
